/**
 * Supplier Ecosystem Ethics Service
 * 
 * Provides comprehensive supplier ecosystem ethics management including:
 * - Supplier dashboard and performance monitoring
 * - Supplier onboarding process and ethics verification
 * - Performance monitoring and KPI tracking
 * - Communication hub for supplier engagement
 * - Ethical sourcing and compliance management
 * - Supplier relationship management and development
 * - Risk assessment and mitigation across supplier network
 * - Sustainability and ESG performance tracking
 */

const SUPPLIER_ECOSYSTEM_KEY = 'supplier-ecosystem-ethics-v1';

// Supplier Categories and Tiers
const SUPPLIER_CATEGORIES = {
  'Tier 1': {
    description: 'Direct suppliers providing finished goods or critical components',
    requirements: ['Full ESG compliance', 'Annual audits', 'Code of conduct signature', 'Sustainability reporting'],
    riskLevel: 'High'
  },
  'Tier 2': {
    description: 'Secondary suppliers providing components to Tier 1 suppliers',
    requirements: ['Basic ESG compliance', 'Bi-annual assessments', 'Ethics training', 'Basic reporting'],
    riskLevel: 'Medium'
  },
  'Tier 3': {
    description: 'Raw material and commodity suppliers',
    requirements: ['Compliance verification', 'Annual assessments', 'Risk monitoring'],
    riskLevel: 'Medium'
  }
};

// Supplier Performance KPIs
const PERFORMANCE_KPIS = {
  quality: {
    name: 'Quality Performance',
    metrics: ['Defect Rate', 'On-time Delivery', 'Customer Satisfaction', 'Product Compliance'],
    weight: 25
  },
  ethics: {
    name: 'Ethics & Compliance',
    metrics: ['Code of Conduct Compliance', 'Audit Score', 'Violation Count', 'Training Completion'],
    weight: 30
  },
  sustainability: {
    name: 'Sustainability',
    metrics: ['Carbon Footprint', 'Waste Reduction', 'Energy Efficiency', 'Circular Economy'],
    weight: 25
  },
  business: {
    name: 'Business Performance',
    metrics: ['Financial Health', 'Innovation', 'Risk Management', 'Partnership Quality'],
    weight: 20
  }
};

// Onboarding Process Stages
const ONBOARDING_STAGES = {
  initial_screening: {
    name: 'Initial Screening',
    duration: '3-5 days',
    requirements: ['Basic company information', 'Financial verification', 'Legal status check']
  },
  ethics_assessment: {
    name: 'Ethics Assessment',
    duration: '1-2 weeks',
    requirements: ['Code of conduct review', 'Ethics questionnaire', 'Reference checks']
  },
  capability_review: {
    name: 'Capability Review',
    duration: '2-3 weeks',
    requirements: ['Technical assessment', 'Quality audit', 'Capacity evaluation']
  },
  contract_negotiation: {
    name: 'Contract Negotiation',
    duration: '1-2 weeks',
    requirements: ['Terms agreement', 'SLA definition', 'Compliance requirements']
  },
  final_approval: {
    name: 'Final Approval',
    duration: '3-5 days',
    requirements: ['Executive approval', 'System setup', 'Kickoff meeting']
  }
};

// Generate comprehensive supplier ecosystem data
function generateSupplierEcosystemData() {
  const data = {
    // Dashboard Overview
    dashboardOverview: {
      totalSuppliers: 258,
      activeSuppliers: 252,
      ethicsCertified: 248,
      underReview: 6,
      suspended: 0,
      complianceRate: 97.8,
      averagePerformance: 93.1,
      lastUpdated: new Date().toISOString()
    },
    
    // Supplier Performance Data
    supplierPerformance: [],
    
    // Onboarding Pipeline
    onboardingPipeline: [],
    
    // Performance Monitoring
    performanceMonitoring: [],
    
    // Communication Activities
    communicationHub: [],
    
    // Risk Assessments
    riskAssessments: [],
    
    // Supplier Development Programs
    developmentPrograms: [],
    
    // Performance Trends
    performanceTrends: [],
    
    // Alerts and Notifications
    alerts: [],
    
    // Supplier Feedback
    supplierFeedback: []
  };

  // Generate supplier performance data
  const supplierNames = [
    '1687 Productions', 'AI for Job Security Foundation', 'So Fraiche',
    'African Supplier Network', 'Sexi Cycle', 'Ethical Textiles Group',
    'Clean Production Partners', 'Renewable Resources LLC', 'Quality Components Co',
    'Innovation Materials Ltd', 'Responsible Logistics Inc', 'Precision Manufacturing',
    'Eco-Friendly Supplies', 'Smart Technology Partners', 'Sustainable Packaging Co',
    'Circular Economy Partners', 'Green Chemistry Solutions', 'Advanced Composites Ltd',
    'Clean Energy Components', 'Responsible Electronics Inc'
  ];

  supplierNames.forEach((name, index) => {
    const tier = index < 8 ? 'Tier 1' : index < 15 ? 'Tier 2' : 'Tier 3';
    const category = ['Manufacturing', 'Technology', 'Raw Materials', 'Services', 'Logistics'][index % 5];
    const qualityScore = 75 + Math.random() * 20;
    const ethicsScore = 80 + Math.random() * 15;
    const sustainabilityScore = 70 + Math.random() * 25;
    const businessScore = 78 + Math.random() * 18;
    const overallScore = (qualityScore * 0.25) + (ethicsScore * 0.3) + (sustainabilityScore * 0.25) + (businessScore * 0.2);
    
    data.supplierPerformance.push({
      id: `supplier_${String(index + 1).padStart(3, '0')}`,
      name,
      tier,
      category,
      location: ['North America', 'Europe', 'Asia-Pacific', 'Latin America'][index % 4],
      relationship: tier === 'Tier 1' ? 'Strategic Partner' : tier === 'Tier 2' ? 'Preferred Supplier' : 'Approved Vendor',
      overallScore: Number(overallScore.toFixed(1)),
      kpiScores: {
        quality: Number(qualityScore.toFixed(1)),
        ethics: Number(ethicsScore.toFixed(1)),
        sustainability: Number(sustainabilityScore.toFixed(1)),
        business: Number(businessScore.toFixed(1))
      },
      riskLevel: overallScore >= 85 ? 'Low' : overallScore >= 75 ? 'Medium' : 'High',
      contractValue: Math.floor(Math.random() * 10000000 + 500000),
      lastAssessment: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      nextAssessment: new Date(Date.now() + Math.random() * 180 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      status: overallScore >= 80 ? 'Active' : overallScore >= 65 ? 'Monitoring' : 'Under Review',
      keyIssues: generateSupplierIssues(overallScore),
      strongPoints: generateSupplierStrengths(),
      certifications: generateCertifications(),
      communicationScore: Number((85 + Math.random() * 10).toFixed(1)),
      responsiveness: ['Excellent', 'Good', 'Average', 'Needs Improvement'][Math.floor(Math.random() * 4)]
    });
  });

  // Generate onboarding pipeline
  const onboardingSuppliers = [
    'New Tech Solutions', 'Green Innovations Ltd', 'Sustainable Goods Inc', 'Eco Materials Corp',
    'Advanced Systems Group', 'Ethical Components Co', 'Clean Manufacturing LLC', 'Smart Solutions Inc'
  ];

  onboardingSuppliers.forEach((name, index) => {
    const stages = Object.keys(ONBOARDING_STAGES);
    const currentStageIndex = Math.floor(Math.random() * stages.length);
    const currentStage = stages[currentStageIndex];
    const progress = (currentStageIndex / (stages.length - 1)) * 100;
    
    data.onboardingPipeline.push({
      id: `onboard_${index + 1}`,
      supplierName: name,
      category: ['Technology', 'Manufacturing', 'Services', 'Materials'][index % 4],
      currentStage,
      progress: Number(progress.toFixed(1)),
      startDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      estimatedCompletion: new Date(Date.now() + Math.random() * 45 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      priority: ['High', 'Medium', 'Low'][Math.floor(Math.random() * 3)],
      assignedTo: ['Sarah Johnson', 'Mike Chen', 'Lisa Rodriguez', 'David Kim'][Math.floor(Math.random() * 4)],
      potentialValue: Math.floor(Math.random() * 5000000 + 100000),
      completedStages: stages.slice(0, currentStageIndex),
      nextMilestone: stages[Math.min(currentStageIndex + 1, stages.length - 1)],
      blockers: currentStageIndex < 3 ? [] : generateOnboardingBlockers(),
      documents: generateOnboardingDocuments(currentStageIndex)
    });
  });

  // Generate performance monitoring alerts
  data.performanceMonitoring = [
    {
      id: 'perf_001',
      type: 'Quality Alert',
      severity: 'High',
      supplier: 'Global Manufacturing Solutions',
      metric: 'Defect Rate',
      currentValue: '3.2%',
      threshold: '2.0%',
      trend: 'Increasing',
      duration: '2 weeks',
      impact: 'Production delays possible',
      actions: ['Schedule quality audit', 'Review production processes', 'Implement corrective measures']
    },
    {
      id: 'perf_002',
      type: 'Ethics Compliance',
      severity: 'Medium',
      supplier: 'EcoTech Components Ltd',
      metric: 'Training Completion',
      currentValue: '78%',
      threshold: '85%',
      trend: 'Stable',
      duration: '1 week',
      impact: 'Compliance gap identified',
      actions: ['Send training reminders', 'Schedule follow-up', 'Monitor progress']
    },
    {
      id: 'perf_003',
      type: 'Sustainability',
      severity: 'Low',
      supplier: 'Sustainable Materials Corp',
      metric: 'Carbon Footprint',
      currentValue: '105 tCO2e',
      threshold: '100 tCO2e',
      trend: 'Improving',
      duration: '3 days',
      impact: 'Minor sustainability target miss',
      actions: ['Review improvement plan', 'Update targets', 'Continue monitoring']
    }
  ];

  // Generate communication hub activities
  data.communicationHub = [
    {
      id: 'comm_001',
      type: 'Quarterly Review',
      supplier: 'Global Manufacturing Solutions',
      date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      participants: ['John Smith (Procurement)', 'Sarah Wilson (Quality)', 'Mike Chen (Supplier)'],
      agenda: ['Performance review', 'Contract renewal', 'Improvement initiatives'],
      status: 'Scheduled',
      priority: 'High'
    },
    {
      id: 'comm_002',
      type: 'Training Session',
      supplier: 'Multiple Suppliers',
      date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      participants: ['15 supplier representatives', 'Ethics team'],
      agenda: ['Ethics compliance update', 'New requirements', 'Q&A session'],
      status: 'Confirmed',
      priority: 'Medium'
    },
    {
      id: 'comm_003',
      type: 'Issue Resolution',
      supplier: 'Advanced Electronics Inc',
      date: new Date().toISOString().slice(0, 10),
      participants: ['Lisa Rodriguez (Procurement)', 'David Kim (Legal)', 'Supplier QA Manager'],
      agenda: ['Quality issue discussion', 'Root cause analysis', 'Corrective action plan'],
      status: 'Completed',
      priority: 'High'
    }
  ];

  // Generate risk assessments
  data.riskAssessments = [
    {
      supplier: 'Global Manufacturing Solutions',
      riskCategory: 'Operational',
      riskLevel: 'Medium',
      probability: 35.5,
      impact: 67.8,
      riskScore: 24.1,
      description: 'Potential production capacity constraints during peak season',
      mitigation: 'Diversify supplier base, establish backup capacity agreements',
      owner: 'Procurement Team',
      lastReview: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      nextReview: new Date(Date.now() + 75 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    },
    {
      supplier: 'EcoTech Components Ltd',
      riskCategory: 'Financial',
      riskLevel: 'Low',
      probability: 15.2,
      impact: 45.3,
      riskScore: 6.9,
      description: 'Minor cash flow challenges due to market conditions',
      mitigation: 'Monitor financial health, consider payment term adjustments',
      owner: 'Finance Team',
      lastReview: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      nextReview: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    },
    {
      supplier: 'Ethical Textiles Group',
      riskCategory: 'Regulatory',
      riskLevel: 'High',
      probability: 78.9,
      impact: 89.4,
      riskScore: 70.5,
      description: 'New labor regulations in operating region may impact compliance',
      mitigation: 'Legal review, compliance audit, policy updates required',
      owner: 'Legal & Compliance',
      lastReview: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      nextReview: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    }
  ];

  // Generate development programs
  data.developmentPrograms = [
    {
      id: 'dev_001',
      name: 'Distribution Partner Impact Accelerator',
      description: 'Comprehensive program integrating 1687 Productions, AI for Job Security Foundation, So Fraiche, African Supplier Network, and Sexi Cycle into the KRO trust ecosystem',
      participants: 58,
      duration: '6 months',
      status: 'Active',
      completion: 88.4,
      objectives: ['Onboard 5 distribution channel partners to KRO Marketplace', 'Integrate ESG metrics across all partner operations', 'Achieve 90+ composite ESG score'],
      outcomes: ['All 5 partners active on Marketplace', '94.2 avg ESG score achieved', '$4.2M fair-trade volume processed', '480+ workers reskilled via AI for Job Security', '15K lbs waste diverted via Sexi Cycle'],
      budget: '$450K',
      spent: '$385K',
      roi: '520%'
    },
    {
      id: 'dev_002',
      name: 'Ethics & Compliance Academy',
      description: 'Training and development program ensuring all distribution partners meet KRO ethics compliance standards',
      participants: 92,
      duration: '3 months',
      status: 'Completed',
      completion: 100,
      objectives: ['100% code of conduct training for all partners', 'Establish ethics reporting channels', 'Implement transparent governance dashboards'],
      outcomes: ['99.4% training completion', 'Zero ethics violations reported', '38% increase in compliance scores', 'All 5 distribution partners certified'],
      budget: '$210K',
      spent: '$195K',
      roi: '680%'
    },
    {
      id: 'dev_003',
      name: 'Circular Economy & Social Impact Initiative',
      description: 'Joint program with Sexi Cycle, So Fraiche, and African Supplier Network to scale circular economy and social impact outcomes',
      participants: 34,
      duration: '12 months',
      status: 'Active',
      completion: 62.5,
      objectives: ['Double waste diversion to 30K lbs', 'Expand fair-trade routes to 9 origins', 'Achieve carbon-neutral supply chain across all consumer goods'],
      outcomes: ['15K lbs textile waste diverted (halfway to target)', '6 origin routes verified', '92% recyclability rate at So Fraiche', '4 retail partnerships activated by Sexi Cycle'],
      budget: '$600K',
      spent: '$340K',
      roi: '410% (projected)'
    }
  ];

  // Generate alerts
  data.alerts = [
    {
      id: 'alert_001',
      type: 'Positive Milestone',
      severity: 'Low',
      message: 'African Supplier Network reached 120+ suppliers onboarded — ahead of Q3 target',
      supplier: 'African Supplier Network',
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      action: 'Celebrate milestone and plan Phase 2 expansion',
      assigned: 'Distribution Team'
    },
    {
      id: 'alert_002',
      type: 'ESG Achievement',
      severity: 'Low',
      message: 'Sexi Cycle achieved 97/100 ESG score — highest among all distribution partners',
      supplier: 'Sexi Cycle',
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      action: 'Feature in quarterly impact report',
      assigned: 'ESG Team'
    },
    {
      id: 'alert_003',
      type: 'Impact Update',
      severity: 'Low',
      message: 'AI for Job Security Foundation: 480+ workers reskilled, 94% employment rate post-program',
      supplier: 'AI for Job Security Foundation',
      dueDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      action: 'Schedule Phase 2 community expansion planning',
      assigned: 'Social Impact Team'
    }
  ];

  // Generate supplier feedback
  data.supplierFeedback = [
    {
      supplier: '1687 Productions',
      feedback: 'Outstanding partnership. KRO trust-based model gives us the creative freedom and transparency we need to deliver authentic impact content.',
      rating: 4.9,
      category: 'Partnership Quality',
      date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      improvements: ['More collaborative planning sessions'],
      strengths: ['Transparent governance', 'Creative autonomy', 'Fair contract terms', 'ESG alignment']
    },
    {
      supplier: 'African Supplier Network',
      feedback: 'KRO ecosystem has opened global market access for our smallholder farmers. Fair-trade pricing and on-chain verification build real trust.',
      rating: 4.8,
      category: 'Communication',
      date: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      improvements: ['More frequent check-ins', 'Clearer performance metrics'],
      strengths: ['Professional team', 'Fair evaluation process']
    },
    {
      supplier: 'So Fraiche',
      feedback: 'The KRO platform enables us to showcase our 92% recyclability rate and zero-waste journey transparently. Consumer trust in our brand has never been higher.',
      rating: 4.7,
      category: 'Development Support',
      date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      improvements: ['More collaborative sustainability workshops'],
      strengths: ['On-chain verification builds consumer trust', 'ESG reporting integration', 'Fair partnership terms']
    },
    {
      supplier: 'Sexi Cycle',
      feedback: 'Being part of KRO Marketplace has amplified our circular economy mission. 15K lbs diverted from landfill and 4 retail partnerships — all verifiable on-chain.',
      rating: 4.9,
      category: 'Impact & Mission',
      date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      improvements: ['Expand waste collection infrastructure support'],
      strengths: ['Mission alignment', 'Transparent impact tracking', 'Community-driven values', 'Net-positive recognition']
    }
  ];

  return data;
}

// Helper functions
function generateSupplierIssues(score) {
  if (score >= 85) return [];
  
  const issues = [
    'Quality control gaps identified',
    'Training compliance below threshold',
    'Sustainability reporting delays',
    'Communication response time issues',
    'Documentation completeness gaps',
    'Process improvement areas identified'
  ];
  
  const count = score < 70 ? 3 : score < 80 ? 2 : 1;
  return issues.slice(0, count);
}

function generateSupplierStrengths() {
  const strengths = [
    'Excellent quality management system',
    'Strong ethical practices',
    'Proactive communication',
    'Continuous improvement mindset',
    'Sustainability leadership',
    'Innovation capabilities',
    'Reliable delivery performance',
    'Technical expertise'
  ];
  
  const count = Math.floor(Math.random() * 3) + 2;
  return strengths.slice(0, count);
}

function generateCertifications() {
  const certs = ['ISO 9001', 'ISO 14001', 'SA8000', 'OHSAS 18001', 'B Corp', 'Fair Trade'];
  const count = Math.floor(Math.random() * 4) + 1;
  return certs.slice(0, count);
}

function generateOnboardingBlockers() {
  const blockers = [
    'Awaiting legal document review',
    'Technical specification clarification needed',
    'Reference verification in progress',
    'Financial documentation pending'
  ];
  
  return blockers.slice(0, Math.floor(Math.random() * 2) + 1);
}

function generateOnboardingDocuments(stageIndex) {
  const allDocs = [
    'Company registration',
    'Financial statements',
    'Insurance certificates',
    'Quality certifications',
    'Ethics questionnaire',
    'Technical specifications',
    'Contract draft',
    'Final agreement'
  ];
  
  return allDocs.slice(0, stageIndex + 2);
}

// State management
function load() {
  try {
    const raw = localStorage.getItem(SUPPLIER_ECOSYSTEM_KEY);
    return raw ? JSON.parse(raw) : generateSupplierEcosystemData();
  } catch {
    return generateSupplierEcosystemData();
  }
}

let state = load();
const subscribers = new Set();

function save() {
  try {
    localStorage.setItem(SUPPLIER_ECOSYSTEM_KEY, JSON.stringify(state));
  } catch {}
}

function publish() {
  save();
  subscribers.forEach(cb => {
    try {
      cb(state);
    } catch {}
  });
}

// Public API
export function subscribe(callback) {
  subscribers.add(callback);
  callback(state);
  return () => subscribers.delete(callback);
}

export function getDashboardOverview() {
  return state.dashboardOverview || {};
}

export function getSupplierPerformance() {
  return state.supplierPerformance || [];
}

export function getOnboardingPipeline() {
  return state.onboardingPipeline || [];
}

export function getPerformanceMonitoring() {
  return state.performanceMonitoring || [];
}

export function getCommunicationHub() {
  return state.communicationHub || [];
}

export function getRiskAssessments() {
  return state.riskAssessments || [];
}

export function getDevelopmentPrograms() {
  return state.developmentPrograms || [];
}

export function getAlerts() {
  return state.alerts || [];
}

export function getSupplierFeedback() {
  return state.supplierFeedback || [];
}

export function getSupplierCategories() {
  return SUPPLIER_CATEGORIES;
}

export function getPerformanceKPIs() {
  return PERFORMANCE_KPIS;
}

export function getOnboardingStages() {
  return ONBOARDING_STAGES;
}

// Analysis functions
export function getTopPerformers() {
  return state.supplierPerformance
    .filter(supplier => supplier.overallScore >= 85)
    .sort((a, b) => b.overallScore - a.overallScore)
    .slice(0, 10);
}

export function getUnderperformers() {
  return state.supplierPerformance
    .filter(supplier => supplier.overallScore < 75)
    .sort((a, b) => a.overallScore - b.overallScore);
}

export function getHighRiskSuppliers() {
  return state.supplierPerformance.filter(supplier => supplier.riskLevel === 'High');
}

export function getCriticalAlerts() {
  return state.alerts.filter(alert => alert.severity === 'High');
}

export function getOnboardingBottlenecks() {
  const stageCounts = {};
  state.onboardingPipeline.forEach(supplier => {
    stageCounts[supplier.currentStage] = (stageCounts[supplier.currentStage] || 0) + 1;
  });
  
  return Object.entries(stageCounts)
    .map(([stage, count]) => ({ stage, count }))
    .sort((a, b) => b.count - a.count);
}

export function getPerformanceTrends() {
  const currentMonth = new Date();
  const lastMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1);
  
  return {
    overallPerformance: {
      current: state.supplierPerformance.reduce((sum, s) => sum + s.overallScore, 0) / state.supplierPerformance.length,
      trend: '+2.3%',
      direction: 'improving'
    },
    ethicsCompliance: {
      current: state.supplierPerformance.reduce((sum, s) => sum + s.kpiScores.ethics, 0) / state.supplierPerformance.length,
      trend: '+1.8%',
      direction: 'improving'
    },
    sustainability: {
      current: state.supplierPerformance.reduce((sum, s) => sum + s.kpiScores.sustainability, 0) / state.supplierPerformance.length,
      trend: '+4.2%',
      direction: 'improving'
    }
  };
}

// Utility functions
export function getSupplierById(id) {
  return state.supplierPerformance.find(supplier => supplier.id === id);
}

export function getSuppliersByTier(tier) {
  return state.supplierPerformance.filter(supplier => supplier.tier === tier);
}

export function getSuppliersByCategory(category) {
  return state.supplierPerformance.filter(supplier => supplier.category === category);
}

export function refreshData() {
  state = generateSupplierEcosystemData();
  publish();
}

export function exportData() {
  return {
    supplierEcosystem: state,
    categories: SUPPLIER_CATEGORIES,
    kpis: PERFORMANCE_KPIS,
    onboardingStages: ONBOARDING_STAGES,
    exportDate: new Date().toISOString()
  };
}